/**
 * @author      carlos.soto@zumper.com (Carlos J. Soto)
 * @copyright   Copyright (c) 2022, Zumper
 * @description Desktop Navbar Wrapper
 */

import { Global, css } from '@emotion/react'
import { ZumperFullDark } from '@zcode-ui/icons'
import { cn } from '@zcode-ui/shadz-ui'
import { keepBetaParam, locationDescriptorToString } from '@zcode/nr-utils'
import React, { useRef } from 'react'
import { useHistory } from 'react-router-dom'
import { TRACK_NAVBAR_ACTION } from '../../../constants/index'
import type { OnNavLinkClickProps } from '../../../types'
import { UserMenu, type UserMenuProps } from '../../UserMenu'

export interface NavWrapperProps
  extends Omit<UserMenuProps, 'isCurrentSearchView' | 'isDetailView'> {
  children?: React.ReactNode
  isCurrentSearchView?: boolean
  isDetailView?: boolean
  isStatic?: boolean
  logo?: React.ReactNode
  menu?: React.ReactNode
  onNavLinkClick?: (props: OnNavLinkClickProps) => void
  isFixed?: boolean
}

const rootHeaderHeightVar = css`
  :root {
    --header-height: 70;
  }
`

export const NavWrapper = ({
  children,
  isCurrentSearchView,
  isDetailView,
  logo,
  menu,
  onNavLinkClick,
  isStatic = false,
  ...rest
}: NavWrapperProps) => {
  const headerRef = useRef<HTMLElement>(null)

  return (
    <>
      <Global styles={rootHeaderHeightVar} />
      {!!isCurrentSearchView && (
        <div className="m-0 p-0">
          <a href="#nav" className="absolute -top-[3em]">
            Skip to navigation
          </a>
          <>
            <a href="#rail" className="absolute -top-[3em]">
              Skip to main content
            </a>
            <a href="#footer" className="absolute -top-[3em]">
              Skip to footer
            </a>
          </>
        </div>
      )}

      <header
        ref={headerRef}
        className={cn(
          'bg-gray-50 border-b border-solid border-gray-150 fixed top-0 right-0 left-0 z-[1030] flex items-start justify-between w-full h-[70px] py-0 px-6 [&:nth-child(2)]:mx-auto',
          isStatic && 'static'
        )}
      >
        <div className="flex items-center self-start h-full max-h-[70px] w-[229px] min-w-[229px] z-[1] max-[975px]:absolute max-[975px]:top-0 max-[975px]:left-6">
          <a
            aria-label="Zumper"
            data-testid="homepage-link"
            href={locationDescriptorToString(keepBetaParam('/'), useHistory())}
            onClick={() =>
              onNavLinkClick?.({
                analyticsEvent: 'Logo',
                trackAction: TRACK_NAVBAR_ACTION.LOGO,
              })
            }
          >
            <div className="flex items-end justify-between">
              {logo ?? <ZumperFullDark size={42} className="-ml-1" />}
            </div>
          </a>
        </div>
        {children}
        <div className="flex items-center self-start h-full max-h-[70px] w-[229px] min-w-[229px] justify-end max-[975px]:absolute max-[975px]:top-0 max-[975px]:right-6">
          {menu ?? (
            <UserMenu
              {...rest}
              isCurrentSearchView={isCurrentSearchView}
              isDetailView={isDetailView}
            />
          )}
        </div>
      </header>
    </>
  )
}
